import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, MobileStepper } from '@mui/material';

const AUTOPLAY_MS = 4000;
const SWIPE_THRESHOLD_RATIO = 0.15; // fraction of slide width
const TAP_MAX_PX = 8;                // movement under this counts as tap, not drag

function openLink(link) {
    if (!link) return;
    if (window.cordova) {
        cordova.InAppBrowser.open(link, "_system");
    } else {
        const w = window.open(link, '_blank');
        if (w) w.focus();
    }
}

const FALLBACK_RATIO = 0.4;       // height/width used until an image loads
const MAX_HEIGHT_VH = 70;         // cap for tall portraits so they don't dominate

function DashboardSlider({ slides = [] }) {
    const cleaned = (slides || []).filter(s => s && s.image);
    const count = cleaned.length;

    const [index, setIndex] = useState(0);
    const [dragPx, setDragPx] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [ratios, setRatios] = useState({}); // { [imageUrl]: height/width }
    const [containerWidth, setContainerWidth] = useState(0);

    const containerRef = useRef(null);
    const startXRef = useRef(0);
    const startYRef = useRef(0);
    const activePointerRef = useRef(null);
    const pausedRef = useRef(false);
    const movedRef = useRef(0);
    const horizontalRef = useRef(null); // null=undecided, true=horizontal swipe, false=vertical scroll

    useEffect(() => {
        if (count <= 1) return;
        const id = setInterval(() => {
            if (!pausedRef.current) {
                setIndex(i => (i + 1) % count);
            }
        }, AUTOPLAY_MS);
        return () => clearInterval(id);
    }, [count]);

    // Preload images to learn their natural aspect ratios so we can size the
    // container to each slide (portrait vs landscape).
    useEffect(() => {
        let cancelled = false;
        cleaned.forEach((slide) => {
            const src = slide.image;
            if (!src || ratios[src]) return;
            const img = new Image();
            img.onload = () => {
                if (cancelled || !img.naturalWidth) return;
                setRatios(prev => prev[src]
                    ? prev
                    : { ...prev, [src]: img.naturalHeight / img.naturalWidth });
            };
            img.src = src;
        });
        return () => { cancelled = true; };
    }, [cleaned.map(s => s.image).join('|')]);

    // Track container width so we can derive pixel height from each slide's ratio.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const update = () => setContainerWidth(el.offsetWidth || 0);
        update();
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(update);
            ro.observe(el);
            return () => ro.disconnect();
        }
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [count]);

    const endDrag = useCallback((commit) => {
        const width = containerRef.current?.offsetWidth || 1;
        let next = index;
        if (commit) {
            const threshold = width * SWIPE_THRESHOLD_RATIO;
            if (dragPx <= -threshold) next = (index + 1) % count;
            else if (dragPx >= threshold) next = (index - 1 + count) % count;
        }
        setIndex(next);
        setDragPx(0);
        setDragging(false);
        pausedRef.current = false;
        activePointerRef.current = null;
        horizontalRef.current = null;
    }, [count, dragPx, index]);

    const onPointerDown = (e) => {
        if (count <= 1) return;
        activePointerRef.current = e.pointerId;
        startXRef.current = e.clientX;
        startYRef.current = e.clientY;
        movedRef.current = 0;
        horizontalRef.current = null;
        pausedRef.current = true;
        setDragging(true);
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    };

    const onPointerMove = (e) => {
        if (activePointerRef.current !== e.pointerId) return;
        const dx = e.clientX - startXRef.current;
        const dy = e.clientY - startYRef.current;

        if (horizontalRef.current === null) {
            if (Math.abs(dx) < TAP_MAX_PX && Math.abs(dy) < TAP_MAX_PX) return;
            horizontalRef.current = Math.abs(dx) >= Math.abs(dy);
            if (!horizontalRef.current) {
                // vertical scroll — release control so the page scrolls
                try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
                activePointerRef.current = null;
                setDragging(false);
                pausedRef.current = false;
                return;
            }
        }

        movedRef.current = Math.max(movedRef.current, Math.abs(dx));
        // prevent the browser from interpreting horizontal drag as a scroll/back gesture
        if (e.cancelable) e.preventDefault();
        setDragPx(dx);
    };

    const onPointerUp = (e) => {
        if (activePointerRef.current !== e.pointerId) return;
        endDrag(true);
    };

    const onPointerCancel = (e) => {
        if (activePointerRef.current !== e.pointerId) return;
        endDrag(false);
    };

    const onSlideClick = (slide) => {
        if (movedRef.current > TAP_MAX_PX) return; // it was a swipe, not a tap
        openLink(slide.link);
    };

    if (count === 0) return null;

    const baseOffsetPct = -index * 100;
    const transform = `translateX(calc(${baseOffsetPct}% + ${dragPx}px))`;

    // Height tracks the visible slide's natural aspect ratio. While dragging,
    // interpolate between the current and the neighbouring slide so the box
    // doesn't snap mid-swipe.
    const heightForIndex = (i) => {
        const src = cleaned[i]?.image;
        const r = (src && ratios[src]) || FALLBACK_RATIO;
        return (containerWidth || 0) * r;
    };
    const maxHeightPx = typeof window !== 'undefined'
        ? window.innerHeight * (MAX_HEIGHT_VH / 100)
        : Infinity;
    const clamp = (h) => Math.min(h, maxHeightPx);
    let stripHeight = clamp(heightForIndex(index));
    if (dragging && containerWidth) {
        const neighbour = dragPx < 0
            ? clamp(heightForIndex((index + 1) % count))
            : clamp(heightForIndex((index - 1 + count) % count));
        const t = Math.min(1, Math.abs(dragPx) / containerWidth);
        stripHeight = stripHeight + (neighbour - stripHeight) * t;
    }

    return (
        <Box
            ref={containerRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { if (!dragging) pausedRef.current = false; }}
            sx={{
                width: '100%',
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                boxShadow: 1,
                backgroundColor: '#f5f5f5',
                touchAction: 'pan-y',
                userSelect: 'none',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: stripHeight ? `${stripHeight}px` : undefined,
                    transition: dragging ? 'none' : 'height 350ms ease-out',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexWrap: 'nowrap',
                        transform,
                        transition: dragging ? 'none' : 'transform 350ms ease-out',
                    }}
                >
                    {cleaned.map((slide, i) => (
                        <Box
                            key={i}
                            onClick={() => onSlideClick(slide)}
                            sx={{
                                flex: '0 0 100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: slide.link ? 'pointer' : 'default',
                                overflow: 'hidden',
                            }}
                            role={slide.link ? 'button' : undefined}
                            aria-label={slide.link ? `Slide ${i + 1}` : undefined}
                        >
                            <Box
                                component="img"
                                src={slide.image}
                                alt=""
                                draggable={false}
                                sx={{
                                    display: 'block',
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
            {count > 1 && (
                <MobileStepper
                    variant="dots"
                    steps={count}
                    position="static"
                    activeStep={index}
                    sx={{
                        background: 'transparent',
                        justifyContent: 'center',
                        py: 0.5,
                        '& .MuiMobileStepper-dot': { mx: 0.3 },
                    }}
                    nextButton={null}
                    backButton={null}
                />
            )}
        </Box>
    );
}

export default DashboardSlider;
